import { useEffect, useState } from "react";
import { Layout } from "../components/layout.tsx";
import { useAuth } from "../context/auth.tsx";
import { listCampaigns, transitionCampaign, type Campaign } from "../api/marketing.ts";

const STATUS_COLOR: Record<string, string> = {
  DRAFT: "bg-gray-700/40 text-gray-300",
  ACTIVE: "bg-green-900/40 text-green-300",
  PAUSED: "bg-yellow-900/40 text-yellow-300",
  COMPLETED: "bg-blue-900/40 text-blue-300",
  ARCHIVED: "bg-gray-800/40 text-gray-500",
};

export function MarketingCampaignsPage() {
  const { token } = useAuth();
  const [items, setItems] = useState<Campaign[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [transitioning, setTransitioning] = useState<string | null>(null);

  const load = () => {
    if (token == null) return;
    setLoading(true);
    listCampaigns(token, { limit: 50 })
      .then((r) => { setItems(r.items); setTotal(r.total); })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : "Failed to load"))
      .finally(() => setLoading(false));
  };

  useEffect(load, [token]);

  async function handleTransition(item: Campaign, status: string) {
    if (token == null) return;
    setTransitioning(item.id);
    try {
      await transitionCampaign(token, item.id, status, item.version);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed");
    } finally {
      setTransitioning(null);
    }
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-white">Campaigns ({total.toLocaleString()})</h1>
        </div>

        {error != null && <p className="text-red-400 text-sm">{error}</p>}
        {loading && <p className="text-gray-400 text-sm">Loading…</p>}

        <div className="space-y-3">
          {items.map((item) => (
            <div key={item.id} className="rounded-lg border border-gray-800 p-4 space-y-3">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="font-medium text-white">{item.name}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{item.type} · Created {new Date(item.createdAt).toLocaleDateString()}</p>
                  {item.description != null && <p className="text-sm text-gray-400 mt-1">{item.description}</p>}
                </div>
                <span className={`text-xs px-2 py-0.5 rounded shrink-0 ${STATUS_COLOR[item.status] ?? "bg-gray-700 text-gray-400"}`}>{item.status}</span>
              </div>

              <div className="flex gap-2">
                {item.status === "DRAFT" && (
                  <button disabled={transitioning === item.id} onClick={() => void handleTransition(item, "ACTIVE")}
                    className="text-xs px-3 py-1 rounded bg-green-900/50 text-green-300 hover:bg-green-800/50 disabled:opacity-50">
                    Launch
                  </button>
                )}
                {item.status === "ACTIVE" && (
                  <button disabled={transitioning === item.id} onClick={() => void handleTransition(item, "PAUSED")}
                    className="text-xs px-3 py-1 rounded bg-yellow-900/50 text-yellow-300 hover:bg-yellow-800/50 disabled:opacity-50">
                    Pause
                  </button>
                )}
                {item.status === "PAUSED" && (
                  <button disabled={transitioning === item.id} onClick={() => void handleTransition(item, "ACTIVE")}
                    className="text-xs px-3 py-1 rounded bg-green-900/50 text-green-300 hover:bg-green-800/50 disabled:opacity-50">
                    Resume
                  </button>
                )}
                {(item.status === "ACTIVE" || item.status === "PAUSED") && (
                  <button disabled={transitioning === item.id} onClick={() => void handleTransition(item, "COMPLETED")}
                    className="text-xs px-3 py-1 rounded bg-blue-900/50 text-blue-300 hover:bg-blue-800/50 disabled:opacity-50">
                    Complete
                  </button>
                )}
              </div>
            </div>
          ))}
          {items.length === 0 && !loading && (
            <p className="text-center text-gray-500 py-8">No campaigns found.</p>
          )}
        </div>
      </div>
    </Layout>
  );
}
